// Safe Synchronization Service
// Prevents data overwrites by using timestamp-based conflict resolution

import { loadPricingConfig } from './pricingConfigServiceRDS';
import { getRecentPricingChanges } from './pricingService';

class SyncService {
  constructor() {
    this.lastSyncTime = null;
    this.localChanges = new Map(); // Track local changes that haven't been synced
    this.remoteChanges = new Map(); // Track remote changes
  }

  // Get the last sync timestamp from localStorage
  getLastSyncTime() {
    const stored = localStorage.getItem('nlfx_last_sync_time');
    return stored ? new Date(stored) : null;
  }

  // Set the last sync timestamp
  setLastSyncTime(timestamp = new Date()) {
    this.lastSyncTime = timestamp;
    localStorage.setItem('nlfx_last_sync_time', timestamp.toISOString());
  }

  // Check for remote changes since last sync
  async checkForRemoteChanges() {
    try {
      const lastSync = this.getLastSyncTime();
      const hours = lastSync ? Math.ceil((Date.now() - lastSync.getTime()) / (1000 * 60 * 60)) : 24;
      
      console.log(`🔄 Checking for remote changes in the last ${hours} hours...`);
      
      const changes = await getRecentPricingChanges(hours);
      
      if (changes && changes.changes && changes.changes.length > 0) {
        console.log(`📊 Found ${changes.changes.length} remote changes`);
        return {
          hasChanges: true,
          changes: changes.changes,
          lastChecked: changes.lastChecked,
          period: changes.period
        };
      }
      
      return {
        hasChanges: false,
        changes: [],
        lastChecked: new Date().toISOString(),
        period: `${hours} hours`
      };
    } catch (error) {
      console.error('❌ Error checking for remote changes:', error);
      throw error;
    }
  }

  // Safe sync strategy - prevents overwriting newer data
  async performSafeSync() {
    try {
      console.log('🔄 Starting safe synchronization...');
      
      // 1. Check for remote changes
      const remoteChanges = await this.checkForRemoteChanges();
      
      if (!remoteChanges.hasChanges) {
        console.log('✅ No remote changes found, sync complete');
        this.setLastSyncTime();
        return {
          success: true,
          message: 'No remote changes to sync',
          conflicts: [],
          synced: false
        };
      }

      // 2. Load current local config
      const localConfig = await loadPricingConfig(false); // Load from S3 (current live data)
      
      // 3. Identify potential conflicts
      const conflicts = this.identifyConflicts(localConfig, remoteChanges.changes);
      
      if (conflicts.length > 0) {
        console.warn(`⚠️ Found ${conflicts.length} potential conflicts`);
        return {
          success: false,
          message: 'Conflicts detected - manual review required',
          conflicts: conflicts,
          synced: false,
          requiresReview: true
        };
      }

      // 4. Perform safe sync (no conflicts detected)
      console.log('✅ No conflicts detected, performing safe sync');
      this.setLastSyncTime();
      
      return {
        success: true,
        message: 'Sync completed successfully',
        conflicts: [],
        synced: true,
        changesApplied: remoteChanges.changes.length
      };

    } catch (error) {
      console.error('❌ Sync failed:', error);
      throw error;
    }
  }

  // Identify potential conflicts between local and remote changes
  identifyConflicts(localConfig, remoteChanges) {
    const conflicts = [];
    
    // Check for conflicts in pricing data
    remoteChanges.forEach(change => {
      if (change.table === 'pricing_config') {
        const conflict = this.checkPricingConflict(localConfig, change);
        if (conflict) {
          conflicts.push(conflict);
        }
      } else if (change.table === 'cable_base_pricing') {
        const conflict = this.checkCablePricingConflict(localConfig, change);
        if (conflict) {
          conflicts.push(conflict);
        }
      }
    });
    
    return conflicts;
  }

  // Check for pricing conflicts
  checkPricingConflict(localConfig, change) {
    const { record_id, newValues, timestamp } = change;
    
    // Find the corresponding local pricing entry
    const localEntry = this.findLocalPricingEntry(localConfig, record_id);
    
    if (!localEntry) {
      return null; // No local entry to conflict with
    }
    
    // Check if local entry has been modified since the remote change
    const localModified = new Date(localEntry.updated_at || localEntry.created_at);
    const remoteModified = new Date(timestamp);
    
    if (localModified > remoteModified) {
      return {
        type: 'pricing_conflict',
        recordId: record_id,
        table: 'pricing_config',
        localValue: localEntry,
        remoteValue: newValues,
        localTimestamp: localModified,
        remoteTimestamp: remoteModified,
        severity: 'warning',
        message: `Local pricing for ${record_id} was modified after remote change`
      };
    }
    
    return null;
  }

  // Check for cable pricing conflicts
  checkCablePricingConflict(localConfig, change) {
    const { record_id, newValues, timestamp } = change;
    
    // Find the corresponding local cable pricing entry
    const localEntry = this.findLocalCablePricingEntry(localConfig, record_id);
    
    if (!localEntry) {
      return null; // No local entry to conflict with
    }
    
    // Check if local entry has been modified since the remote change
    const localModified = new Date(localEntry.updated_at || localEntry.created_at);
    const remoteModified = new Date(timestamp);
    
    if (localModified > remoteModified) {
      return {
        type: 'cable_pricing_conflict',
        recordId: record_id,
        table: 'cable_base_pricing',
        localValue: localEntry,
        remoteValue: newValues,
        localTimestamp: localModified,
        remoteTimestamp: remoteModified,
        severity: 'warning',
        message: `Local cable pricing for ${record_id} was modified after remote change`
      };
    }
    
    return null;
  }

  // Helper methods to find local entries
  findLocalPricingEntry(config, recordId) {
    // Search in panel mount pricing
    if (config.panelMountPricing && config.panelMountPricing[recordId]) {
      return config.panelMountPricing[recordId];
    }
    
    // Search in inline pricing
    if (config.inlinePricing && config.inlinePricing[recordId]) {
      return config.inlinePricing[recordId];
    }
    
    return null;
  }

  findLocalCablePricingEntry(config, recordId) {
    if (config.cableBasePrices && config.cableBasePrices[recordId]) {
      return config.cableBasePrices[recordId];
    }
    
    return null;
  }

  // Get sync status
  getSyncStatus() {
    const lastSync = this.getLastSyncTime();
    return {
      lastSyncTime: lastSync,
      lastSyncAgo: lastSync ? Math.floor((Date.now() - lastSync.getTime()) / (1000 * 60)) : null,
      needsSync: !lastSync || (Date.now() - lastSync.getTime()) > (5 * 60 * 1000) // 5 minutes
    };
  }

  // Force sync (admin only - bypasses conflict detection)
  async forceSync() {
    try {
      console.log('🔄 Performing force sync (admin override)...');
      
      // Load fresh config from RDS
      const freshConfig = await loadPricingConfig(true); // Load from RDS (admin)
      
      // Clear local cache
      localStorage.removeItem('nlfx_pricing_cache');
      localStorage.removeItem('nlfx_last_sync_time');
      
      this.setLastSyncTime();
      
      return {
        success: true,
        message: 'Force sync completed - local cache cleared',
        synced: true,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('❌ Force sync failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const syncService = new SyncService();
export default syncService;
